import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { money, fmtDate } from '../events/util';

const INK = '#9A9A9A';

// Campañas de invitación: llevar a los asistentes de un evento (o a un segmento) a otro.
//
// El flujo es a propósito en este orden: primero A QUIÉN (y cuántos quedan afuera y por
// qué), después el evento y el lote, y recién al final el texto. Elegir el texto antes de
// saber si la audiencia son 20 o 200 personas lleva a escribir para un público que no
// existe.
export default function Invitaciones() {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [campanas, setCampanas] = useState([]);
  const [ver, setVer] = useState(null);     // campaña cuyo detalle se mira
  const [creando, setCreando] = useState(false);

  const load = () => api.get('/api/invitations/campaigns').then(setCampanas).catch(() => {});
  useEffect(() => { api.get('/api/events').then(setEvents).catch(() => {}); load(); }, []); // eslint-disable-line

  if (creando) return <Crear api={api} events={events} onClose={() => { setCreando(false); load(); }} />;
  if (ver) return <Detalle api={api} campana={ver} onClose={() => setVer(null)} />;

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="lb-display" style={{ fontSize: 22 }}>Campañas de invitación</div>
          <p style={{ color: INK, fontSize: 13, marginTop: 4, marginBottom: 0, maxWidth: 620, lineHeight: 1.55 }}>
            Invitá a los asistentes de un evento a otro. Cada persona recibe su link y la entrada se emite <b>recién cuando acepta</b>, así lo que no se acepta no infla la venta.
          </p>
        </div>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setCreando(true)}>+ Nueva invitación</button>
      </div>

      {campanas.length === 0 && (
        <div className="lb-card" style={{ padding: 30, textAlign: 'center', color: INK, fontSize: 13 }}>
          Todavía no hay campañas de invitación.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {campanas.map(c => (
          <div key={c.id} className="lb-card" style={{ padding: '16px 20px', cursor: 'pointer' }} onClick={() => setVer(c)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: INK, marginTop: 2 }}>
                  {c.source_event_name ? `${c.source_event_name} → ` : ''}<b style={{ color: '#fff' }}>{c.event_name}</b>
                  {c.event_date ? ` · ${fmtDate(c.event_date)}` : ''}
                  {c.lot_name ? ` · ${c.lot_name}` : ''}
                  {Number(c.lot_price) ? ` · ${money(c.lot_price)}` : ' · sin cargo'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                <Paso n={c.invitados} t="invitados" />
                <Paso n={c.abrieron} t="abrieron" />
                <Paso n={c.aceptaron} t="aceptaron" fuerte />
                <Paso n={c.vinieron} t="vinieron" />
                <div style={{ textAlign: 'right', minWidth: 74 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: c.conversion >= 15 ? '#2ecc71' : '#fff' }}>{c.conversion}%</div>
                  <div className="lb-eyebrow">conversión</div>
                </div>
              </div>
            </div>
            {(c.vencida || (Number(c.cupo) > 0 && c.aceptaron >= Number(c.cupo))) && (
              <div style={{ fontSize: 11.5, color: '#ffb300', marginTop: 8 }}>
                {c.vencida ? 'Vencida' : ''}{c.vencida && Number(c.cupo) > 0 && c.aceptaron >= Number(c.cupo) ? ' · ' : ''}
                {Number(c.cupo) > 0 && c.aceptaron >= Number(c.cupo) ? `Cupo completo (${c.cupo})` : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Paso({ n, t, fuerte }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 62 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: fuerte ? '#fff' : INK }}>{n}</div>
      <div style={{ fontSize: 10, color: INK, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t}</div>
    </div>
  );
}

/* ─────────────────────────── CREAR ─────────────────────────── */
function Crear({ api, events, onClose }) {
  const [f, setF] = useState({
    event_id: '', lot_id: '', name: '', modo: 'email', cupo: '', horas: 72,
    subject: '', texto: '',
    filtros: { source_event_id: '', min_asistencias: '', edad_min: '', edad_max: '', ciudades: [], compro_y_no_vino: false, dormidos_meses: '' },
  });
  const [lots, setLots] = useState([]);
  const [aud, setAud] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setFiltro = (k, v) => setF(s => ({ ...s, filtros: { ...s.filtros, [k]: v } }));

  useEffect(() => {
    if (!f.event_id) { setLots([]); return; }
    api.get(`/api/events/${f.event_id}/lots`).then(setLots).catch(() => {});
  }, [f.event_id]); // eslint-disable-line

  // La audiencia se recalcula sola: cada cambio de filtro tiene que mostrar al instante
  // a cuánta gente llega, si no se manda a ciegas.
  useEffect(() => {
    if (!f.event_id) { setAud(null); return; }
    setCalculando(true);
    const t = setTimeout(() => {
      api.post('/api/invitations/audiencia', { event_id: f.event_id, modo: f.modo, filtros: f.filtros })
        .then(setAud).catch(() => setAud(null)).finally(() => setCalculando(false));
    }, 350);
    return () => clearTimeout(t);
  }, [f.event_id, f.modo, JSON.stringify(f.filtros)]); // eslint-disable-line

  async function crear() {
    if (!aud?.personas?.length) return;
    if (!window.confirm(`Se van a emitir ${aud.personas.length} invitaciones${f.modo === 'email' ? ' y enviar por email' : ''}. ¿Continuar?`)) return;
    setEnviando(true);
    try {
      const r = await api.post('/api/invitations/campaigns', {
        event_id: f.event_id, lot_id: f.lot_id, name: f.name || 'Invitación',
        source_event_id: f.filtros.source_event_id || null,
        cupo: Number(f.cupo) || 0, horas: Number(f.horas) || 72,
        personas: aud.personas,
        enviar: f.modo === 'email',
        subject: f.subject || undefined,
        html: f.texto ? textoAHtml(f.texto) : undefined,
        audience_filters: f.filtros,
      });
      setHecho(r);
    } catch (e) { alert(e.message); } finally { setEnviando(false); }
  }

  if (hecho) return (
    <div className="lb-fade">
      <div className="lb-card" style={{ padding: 28, textAlign: 'center' }}>
        <div className="lb-display" style={{ fontSize: 24, color: '#2ecc71' }}>Invitaciones emitidas</div>
        <p style={{ color: INK, fontSize: 14, marginTop: 10, lineHeight: 1.6 }}>
          Se generaron <b style={{ color: '#fff' }}>{hecho.invitados}</b> invitaciones
          {hecho.duplicados > 0 && <> (se descartaron {hecho.duplicados} repetidas)</>}.
          {f.modo === 'email'
            ? <> Se enviaron <b style={{ color: '#fff' }}>{hecho.envio?.sent ?? 0}</b> emails.</>
            : <> Podés copiar los links desde el detalle de la campaña y repartirlos.</>}
        </p>
        {f.modo === 'email' && hecho.envio?.skipped === 'no-api-key' && (
          <p style={{ color: '#ffb300', fontSize: 12.5 }}>El envío de emails no está configurado en este entorno, pero las invitaciones ya existen: se pueden repartir a mano.</p>
        )}
        <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 12 }} onClick={onClose}>Volver</button>
      </div>
    </div>
  );

  const listo = f.event_id && f.lot_id && aud?.personas?.length > 0;
  return (
    <div className="lb-fade" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onClose}>← Volver</button>
        <div className="lb-display" style={{ fontSize: 20 }}>Nueva invitación</div>
      </div>

      {/* 1 · A QUIÉN */}
      <div className="lb-card" style={{ padding: '18px 22px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>1 · A quién</div>
        {/* alignItems: end — la etiqueta de fidelidad ocupa dos renglones y sin esto su
            campo quedaba más abajo que los demás. Alineando por abajo, los inputs quedan
            en la misma línea sin importar cuánto ocupe cada etiqueta. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'end' }}>
          <Campo label="Asistentes de un evento">
            <select className="lb-select" value={f.filtros.source_event_id} onChange={e => setFiltro('source_event_id', e.target.value)}>
              <option value="">— Cualquiera —</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </Campo>
          <Campo label="Mínimo de asistencias (año)">
            <input className="lb-input" inputMode="numeric" placeholder="ej. 5" value={f.filtros.min_asistencias}
              onChange={e => setFiltro('min_asistencias', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Edad (a la fecha del evento)">
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="lb-input" inputMode="numeric" placeholder="desde" value={f.filtros.edad_min} onChange={e => setFiltro('edad_min', e.target.value.replace(/\D/g, ''))} />
              <input className="lb-input" inputMode="numeric" placeholder="hasta" value={f.filtros.edad_max} onChange={e => setFiltro('edad_max', e.target.value.replace(/\D/g, ''))} />
            </div>
          </Campo>
          <Campo label="Sin venir hace (meses)">
            <input className="lb-input" inputMode="numeric" placeholder="ej. 3" value={f.filtros.dormidos_meses}
              onChange={e => setFiltro('dormidos_meses', e.target.value.replace(/\D/g, ''))} />
          </Campo>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13, color: 'var(--ink-700)', cursor: 'pointer' }}>
          <input type="checkbox" checked={f.filtros.compro_y_no_vino} onChange={e => setFiltro('compro_y_no_vino', e.target.checked)} />
          Solo los que <b style={{ color: '#fff' }}>compraron y no vinieron</b>
        </label>

        {/* El desglose de exclusiones. Es lo que evita mandar a ciegas: si de 137 quedan
            32, hay que poder ver si es correcto o si falta cargar datos. */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {calculando && <div style={{ fontSize: 13, color: INK }}>Calculando…</div>}
          {!calculando && !f.event_id && <div style={{ fontSize: 13, color: INK }}>Elegí el evento destino abajo para ver a cuánta gente llega.</div>}
          {!calculando && aud && (
            <>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: aud.invitables ? '#fff' : 'var(--red-400)' }}>
                {aud.invitables} invitables
                <span style={{ color: INK, fontSize: 13, fontWeight: 400 }}> de {aud.coinciden} que coinciden</span>
              </div>
              <div style={{ fontSize: 12, color: INK, marginTop: 6, lineHeight: 1.6 }}>
                {[
                  aud.excluidos.menores && `${aud.excluidos.menores} menores de 18`,
                  aud.excluidos.sin_consentimiento && `${aud.excluidos.sin_consentimiento} sin consentimiento`,
                  aud.excluidos.sin_email && `${aud.excluidos.sin_email} sin email`,
                  aud.excluidos.ya_tienen_entrada && `${aud.excluidos.ya_tienen_entrada} ya tienen entrada`,
                  aud.excluidos.ya_invitados && `${aud.excluidos.ya_invitados} ya invitados`,
                ].filter(Boolean).join(' · ') || 'Sin exclusiones.'}
              </div>
              <div style={{ fontSize: 11.5, color: INK, marginTop: 8, lineHeight: 1.5 }}>{aud.nota}</div>
            </>
          )}
        </div>
      </div>

      {/* 2 · A DÓNDE */}
      <div className="lb-card" style={{ padding: '18px 22px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>2 · A qué evento</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <Campo label="Evento destino">
            <select className="lb-select" value={f.event_id} onChange={e => { set('event_id', e.target.value); set('lot_id', ''); }}>
              <option value="">— Elegir —</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </Campo>
          <Campo label="Lote de invitación">
            <select className="lb-select" value={f.lot_id} onChange={e => set('lot_id', e.target.value)}>
              <option value="">— Elegir —</option>
              {lots.map(l => <option key={l.id} value={l.id}>{l.name} · {Number(l.price) ? money(l.price) : 'sin cargo'}</option>)}
            </select>
          </Campo>
          <Campo label="Cupo de aceptadas">
            <input className="lb-input" inputMode="numeric" placeholder="0 = sin tope" value={f.cupo} onChange={e => set('cupo', e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo label="Vence en (horas)">
            <input className="lb-input" inputMode="numeric" value={f.horas} onChange={e => set('horas', e.target.value.replace(/\D/g, ''))} />
          </Campo>
        </div>
        <div style={{ fontSize: 11.5, color: INK, marginTop: 10, lineHeight: 1.5 }}>
          El cupo es de <b>aceptadas</b>, no de invitadas: se invita de más sabiendo que no aceptan todos. El vencimiento nunca pasa del inicio del evento.
        </div>
      </div>

      {/* 3 · CÓMO */}
      <div className="lb-card" style={{ padding: '18px 22px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 12 }}>3 · Cómo se entrega</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <button className={'lb-btn lb-btn--sm ' + (f.modo === 'email' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => set('modo', 'email')}>Por email</button>
          <button className={'lb-btn lb-btn--sm ' + (f.modo === 'mano' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => set('modo', 'mano')}>Links para repartir</button>
        </div>
        {f.modo === 'email' ? (
          <>
            <Campo label="Asunto"><input className="lb-input" value={f.subject} onChange={e => set('subject', e.target.value)} placeholder="Te invitamos a nuestra próxima noche" /></Campo>
            <div style={{ marginTop: 12 }}>
              <Campo label="Mensaje (opcional — si lo dejás vacío se usa uno por defecto)">
                <textarea className="lb-input" rows={5} value={f.texto} onChange={e => set('texto', e.target.value)}
                  placeholder={'Hola {{NOMBRE}}, como viniste a {{EVENTO_ORIGEN}} queremos invitarte a {{EVENTO}}.'} />
              </Campo>
              <div style={{ fontSize: 11.5, color: INK, marginTop: 6 }}>
                Podés usar <b>{'{{NOMBRE}}'}</b>, <b>{'{{EVENTO}}'}</b> y <b>{'{{EVENTO_ORIGEN}}'}</b>. El botón con el link de cada persona se agrega solo.
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.6 }}>
            Se generan los links y los repartís vos (en la puerta, por WhatsApp o dándoselos a quien organiza).
            No se manda ningún email, así que <b style={{ color: '#fff' }}>no aplica el consentimiento</b> y entran también los menores de 18,
            que igual pueden ingresar al boliche.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="lb-btn lb-btn--primary" onClick={crear} disabled={!listo || enviando}>
          {enviando ? 'Emitiendo…' : `Emitir ${aud?.personas?.length || 0} invitaciones`}
        </button>
        <Campo label=""><input className="lb-input" placeholder="Nombre de la campaña" value={f.name} onChange={e => set('name', e.target.value)} /></Campo>
      </div>
    </div>
  );
}

// El texto plano del dueño se convierte en el HTML del email, con el botón del link.
function textoAHtml(texto) {
  const parrafos = String(texto).split(/\n{2,}/).map(p =>
    `<p style="color:#C9C9C9;font-size:15px;line-height:1.6">${p.replace(/\n/g, '<br>')}</p>`).join('');
  return `<div style="background:#000;padding:32px;font-family:Arial,sans-serif;color:#fff">
    ${parrafos}
    <p><a href="{{LINK}}" style="display:inline-block;background:#C8102E;color:#fff;text-decoration:none;font-weight:700;padding:14px 26px;border-radius:6px">Generar mi entrada →</a></p>
    <p style="color:#6B6B6B;font-size:12px">Si no querés recibir más novedades, <a href="{{UNSUB_URL}}" style="color:#6B6B6B">date de baja acá</a>.</p>
  </div>`;
}

/* ─────────────────────────── DETALLE ─────────────────────────── */
function Detalle({ api, campana, onClose }) {
  const [filas, setFilas] = useState(null);
  const [copiado, setCopiado] = useState('');
  useEffect(() => {
    api.get(`/api/invitations/campaigns/${campana.id}/detalle`).then(setFilas).catch(() => setFilas([]));
  }, [campana.id]); // eslint-disable-line

  const base = `${window.location.origin}/i/`;
  const copiarTodos = () => {
    const txt = (filas || []).map(f => `${[f.name, f.lastname].filter(Boolean).join(' ')}\t${base}${f.code}`).join('\n');
    navigator.clipboard?.writeText(txt); setCopiado('todos'); setTimeout(() => setCopiado(''), 1800);
  };

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onClose}>← Volver</button>
        <div className="lb-display" style={{ fontSize: 20 }}>{campana.name}</div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginLeft: 'auto' }} onClick={copiarTodos}>
          {copiado === 'todos' ? 'Copiado ✓' : 'Copiar todos los links'}
        </button>
      </div>

      <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr auto' }}>
          <span>Invitado</span><span>Email</span><span>Estado</span><span>QR</span><span></span>
        </div>
        {filas === null && <div style={{ padding: '20px 0', color: INK, fontSize: 13 }}>Cargando…</div>}
        {(filas || []).map(f => (
          <div key={f.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr auto', alignItems: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{[f.lastname, f.name].filter(Boolean).join(', ') || '—'}</span>
            <span style={{ color: INK, fontSize: 12.5 }}>{f.email || '—'}</span>
            <span>
              {f.status === 'aceptada'
                ? <span className="lb-status lb-status--paid">Aceptó</span>
                : f.opened_at
                  ? <span className="lb-status lb-status--gen">Abrió</span>
                  : <span style={{ fontSize: 12, color: INK }}>Sin abrir</span>}
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: f.qr_code ? 'var(--red-400)' : INK }}>
              {f.qr_code || '—'}{f.ticket_status === 'usado' ? ' · entró' : ''}
            </span>
            <span style={{ textAlign: 'right' }}>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { navigator.clipboard?.writeText(base + f.code); setCopiado(f.id); setTimeout(() => setCopiado(''), 1500); }}>
                {copiado === f.id ? 'Copiado ✓' : 'Copiar link'}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return <div className="lb-field" style={{ flex: 1 }}>{label ? <div className="lb-label">{label}</div> : null}{children}</div>;
}
